import React, { useCallback, useEffect, useState, useRef } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import {
  ObjValidator,
  ProjectTree,
  TranslateWrapper,
  XCircleLoader,
} from "components";
import { getHomepage } from "utils";
import { ProductListWithControls } from "components/ProductListOfProject/ProductListWithControls";
import { ProjectHeader } from "components/ProjectHeader/ProjectHeader";
import { Clipboard } from "components/ProjectTree/Clipboard";
import { ToggleShowPictures } from "components/ProjectTree/ToggleShowPictures";
import { ExpandCollapseLevels } from "components/ProjectTree/ExpandCollapseLevels";
import { useTranslate } from "hooks";
import {
  useProjectError,
  useProject,
  useIsProjectLoading,
} from "stores/project";
import { PROJECT_CONFIGURATOR_TAB_TYPES } from "variables";
import type { ObjValidatorProps } from "interfaces/shared.types";
import type { ProductListRefValue } from "interfaces/product/product.types";

const ProjectConfigurator = Scrivito.connect(({ page }: ObjValidatorProps) => {
  const isLoading = useIsProjectLoading();
  const translate = useTranslate();
  const [activeTab, setActiveTab] = useState(
    PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE,
  );
  const productListRef = useRef<ProductListRefValue>(null);

  const handleChangeActiveTab = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      setActiveTab(e.currentTarget.id);
    },
    [],
  );

  useRedirectEffect();

  const projectSummarySidebarMyStructure = translate(
    "PROJECT_SUMMARY_SIDEBAR_MY_STRUCTURE",
  );
  const projectProductListTitle = translate("PROJECT_PRODUCT_LIST");

  if (isLoading) {
    return <XCircleLoader />;
  }

  if (!page) {
    return null;
  }

  return (
    <>
      <div className="pt-4 h-full d-flex flex-column overflow-auto">
        <div className="ms-3 px-md-5 px-lg-9">
          <div className="position-relative">
            <div className="d-flex">
              <ProjectHeader page={page} productListRef={productListRef} />
            </div>
            <Nav
              tabs
              className="d-inline-flex border border-info p-1 rounded-3 me-auto mt-4"
            >
              <NavItem>
                <NavLink
                  id={PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE}
                  onClick={handleChangeActiveTab}
                  className={cx("rounded-1 cursor-pointer py-1 fs-18 lh-22", {
                    "bg-blue-300 text-white":
                      activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE,
                    "text-blue-300":
                      activeTab !== PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE,
                  })}
                >
                  {translate.stringOnly(
                    "PROJECT_SUMMARY_SIDEBAR_MY_STRUCTURE",
                    null,
                    true,
                  )}
                </NavLink>
              </NavItem>
              {typeof projectSummarySidebarMyStructure === "object" && (
                <TranslateWrapper
                  tooltipText={
                    projectSummarySidebarMyStructure.props.tooltipText
                  }
                  tooltipId={PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE}
                />
              )}
              <NavItem>
                <NavLink
                  id={PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST}
                  onClick={handleChangeActiveTab}
                  className={cx("rounded-1 cursor-pointer py-1 fs-18 lh-22", {
                    "bg-blue-300 text-white":
                      activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST,
                    "text-blue-300":
                      activeTab !== PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST,
                  })}
                >
                  {translate.stringOnly("PROJECT_PRODUCT_LIST", null)}
                </NavLink>
              </NavItem>
              {typeof projectProductListTitle === "object" && (
                <TranslateWrapper
                  tooltipText={projectProductListTitle.props.tooltipText}
                  tooltipId={PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST}
                />
              )}
            </Nav>
            {activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE && (
              <div className="d-flex flex-row align-items-center justify-content-between pt-3">
                <ExpandCollapseLevels />
                <ToggleShowPictures />
              </div>
            )}

            <Clipboard />
          </div>
        </div>
        <TabContent
          activeTab={activeTab}
          className="d-flex flex-column justify-content-start h-100 ms-3"
        >
          <TabPane
            tabId={PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE}
            className={cx("d-flex", {
              "h-100 mt-2 w-100 ps-md-5 ps-lg-9":
                activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE,
            })}
          >
            {activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE && (
              <ProjectTree options={{ isFullpage: true }} />
            )}
          </TabPane>
          <TabPane
            tabId={PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST}
            className="px-md-5 px-lg-9"
          >
            {activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST && (
              <ProductListWithControls
                /* TODO: forthcoming feature (hidden acc US-19953) set "hasFilter={false}"  */
                hasFilter={false}
                hasSizeSwitcher
                productListRef={productListRef}
              />
            )}
          </TabPane>
        </TabContent>
        <Scrivito.ContentTag content={page} attribute="body" tag="div" />
      </div>
    </>
  );
});

const CheckedPage = (props: ObjValidatorProps) => (
  <ObjValidator {...props}>
    <ProjectConfigurator {...props} />
  </ObjValidator>
);
Scrivito.provideComponent("ProjectConfigurator", CheckedPage);

export default CheckedPage;

const useRedirectEffect = () => {
  const error = useProjectError();
  const project = useProject();
  const isLoading = useIsProjectLoading();
  useEffect(() => {
    if ((error || !project?.id) && !isLoading) {
      const redirect = async () => {
        const { welcomePageObj } = await Scrivito.load(() => {
          const homepage = getHomepage();
          const welcomePage = homepage?.get("welcomePage");
          return {
            welcomePageObj: welcomePage || homepage,
          };
        });

        Scrivito.navigateTo(welcomePageObj as Scrivito.Obj);
      };
      redirect();
    }
  }, [error, project, isLoading]);
};
